<template>
  <div class="chat">
    <div v-if="$slots.warning" class="chat__text chat__text_warning">
      <slot name="warning"></slot>
    </div>

    <div v-if="$slots.info" class="chat__text">
      <slot name="info"></slot>
    </div>

    <template v-if="$slots.lobby">
      <slot name="lobby"></slot>
    </template>

    <div class="chat__wrap">
      <div class="chat__header mb-4">
        <slot name="header">
          <div v-if="!$slots.header" class="title">Чат</div>
        </slot>
      </div>

      <ChatMessages
        :player1="player1"
        :player2="player2"
        :history="messages"
        class="container fluid grid-list-lg pa-0"
        ref="chatMessages"
      />
    </div>

    <ChatForm v-if="canWrite" :room="room" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import cloneDeep from "lodash/cloneDeep";

import goTo from "@/core/util/goTo";
import matchService from "@/services/match";
import ioService from "@/services/io";

import ChatMessages from "./ChatMessages";
import ChatForm from "./ChatForm";

export default {
  components: { ChatMessages, ChatForm },
  props: {
    canWrite: Boolean,
    player1: {
      type: Object,
      default: () => null
    },
    player2: {
      type: Object,
      default: () => null
    },
    room: {
      type: [Number, String],
      required: true
    },
    participant: Boolean
  },

  data() {
    return {
      msgTimer: null,
      historyInited: false,
      chatHistory: []
    };
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapGetters("notifications", ["isConnected"]),

    socketRoom() {
      return `match${this.room}`;
    },

    messages() {
      let list = cloneDeep(this.chatHistory);
      list.sort((a, b) => a.time - b.time);
      return list;
    }
  },

  watch: {
    socketRoom: "socketInit",

    isAuthenticated() {
      this.chatHistory = [];
      this.historyInited = false;
    },

    isConnected(after) {
      this.$nextTick(() => {
        if (!after) {
          this.chatHistory = [];
          this.historyInited = false;
          ioService.leaveRoom(this.socketRoom);
        } else {
          ioService.joinRoom(this.socketRoom);
        }
      });
    },

    chatHistory(after) {
      if (!after || !after.length) return;

      if (this.msgTimer) clearTimeout(this.msgTimer);

      let duration = this.historyInited && after.length > 0 ? 1000 : 0;

      this.msgTimer = setTimeout(() => {
        try {
          goTo(".message:last-child", {
            container: ".chat__messages .simplebar-content-wrapper",
            duration
          });
        } catch (e) {
          console.log(e);
        }
      }, 250);
    }
  },

  sockets: {
    async history({ history }) {
      let list = history.map(async msg => {
        if (msg.attachment) {
          msg.attachment = await this.getAttachment(msg.attachment);
        }
        return msg;
      });
      list = await Promise.all(list);
      this.chatHistory = list;

      this.$nextTick(() => {
        this.historyInited = true;
      });
    },

    async new_message(msg) {
      if (msg.attachment) {
        msg.attachment = await this.getAttachment(msg.attachment);
      }
      this.chatHistory.push(msg);
    }
  },

  beforeDestroy() {
    ioService.leaveRoom(this.socketRoom);
  },

  mounted() {
    this.socketInit(this.socketRoom);
  },

  methods: {
    getAttachment(attachment) {
      return matchService.getAttachment(+attachment || +attachment.id);
    },

    socketInit(roomAfter, roomBefore) {
      this.chatHistory = [];
      this.historyInited = false;

      let room = roomAfter ? roomAfter : this.socketRoom;

      if (roomBefore && roomAfter !== roomBefore)
        ioService.leaveRoom(roomBefore);

      if (room) {
        ioService.joinRoom(room);
      }
    }
  }
};
</script>

<style lang="scss">
.chat {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;

  .title {
    font-weight: 900;
    font-size: 28px;
    line-height: 34px;
    color: #fff;
    text-align: center;
  }

  &__header {
    position: relative;

    .invite_admin {
      position: absolute;
      right: 0;
      top: 0;
    }
  }

  &__wrap {
    background: rgba(#fff, 0.05);
    border-radius: 4px;
    padding: 30px;
    width: 100%;

    .in_future:not(.can_protest) & {
      min-height: 100%;
    }
  }

  &__messages {
    width: 100%;
    max-height: 500px;

    &_empty {
      text-align: center;
      padding: 30px;

      &.chat__text {
        font-size: 18px;
        line-height: 30px;
      }
    }

    .simplebar-track {
      right: -20px;

      @include breakpoint("xs-only") {
        right: -10px;
      }
    }
  }

  &__form {
    margin-top: 30px;
    position: relative;

    .divider {
      margin: 30px 0;
      opacity: 0.18;
    }

    &_actions {
      display: flex;
      flex-direction: row;
      margin-top: 30px;

      .btn {
        width: 225px;
      }
    }

    @include breakpoint("xs-only") {
      &_actions {
        margin: 0 -16px;
        padding: 8px;
        flex-direction: column;

        .btn {
          width: auto;
          margin: 8px;
        }
      }
    }

    .text-field {
      padding: 16px 20px;
    }
  }

  &__text {
    font-size: 14px;
    line-height: 19px;
    margin: 0 auto;
    margin-bottom: 30px;
    width: 100%;
    max-width: 580px;
    color: #7f8492;

    .chat__messages & {
      margin-bottom: 0px;
    }

    &_warning {
      background: rgba(231, 47, 49, 0.1);
      padding: 48px 30px;
      border: 2px solid #e72f31;
      box-sizing: border-box;
      border-radius: 4px;

      color: #e72f31;
      text-shadow: 0px 0px 15px rgba(255, 0, 0, 0.5);
      font-size: 15px;
      line-height: 18px;
      text-align: center;

      .title {
        color: #e72f31;
        font-weight: bold;
        font-size: 36px;
        line-height: 42px;
        text-transform: uppercase;
      }

      @include breakpoint("xs-only") {
        padding: 20px 16px;

        p {
          text-align: left;
          margin-bottom: 0;
          margin-top: 20px;

          &.title {
            font-size: 28px;
            line-height: 34px;
            margin-top: 0;
            text-align: center !important;
          }
        }
      }
    }
  }
}

@include breakpoint("xs-only") {
  .team-columns {
    .chat {
      order: 2;
      margin: 0;
      margin-top: 30px;
      width: 100%;

      .title {
        text-align: left;
      }

      &__wrap {
        padding: 16px;
      }
    }
  }
}
</style>
